import { useEffect, useState } from "react";
import styled, { css } from "styled-components";
import { ReactComponent as BackIcon } from "../assets/icons/backIcon.svg";
import { ReactComponent as SearchIcon } from "../assets/icons/search.svg";
import useBookStore from "../store/useBookStore";
import { useNavigate } from "react-router-dom";
import { bookAPI } from "../lib/api";
import LoadingPage from "./LoadingPage";
import instance from "../lib/axios";

export default function SearchPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isSearched, setIsSearched] = useState(false);
  const { scannedBooks } = useBookStore();
  const [recommendBook, setRecommendBook] = useState(null);
  const navigate = useNavigate();

  const onBack = () => {
    navigate(-1);
  };

  const handleBookClick = (isbn) => {
    navigate(`/search/book/info/${isbn}`);
  };

  const fetchRecommendBook = async () => {
    try {
      const response = await instance.get("preferences/recommendations/", {
        params: { mode: "combined" },
      });

      const recommendedBookData = response.data?.results;


      if (
        recommendedBookData &&
        Array.isArray(recommendedBookData) &&
        recommendedBookData.length > 0
      ) {
        const randomIndex = Math.floor(
          Math.random() * recommendedBookData.length
        );
        setRecommendBook(recommendedBookData[randomIndex]);
      } else {
        setRecommendBook(null);
      }
    } catch (error) {
      console.error("추천 도서 불러오기 실패: ", error);
      setRecommendBook(null);
    }
  };


  useEffect(() => {
    if (searchQuery && searchQuery.trim().length > 0) {
      setLoading(true);
      const timer = setTimeout(async () => {
        try {
          const data = await bookAPI.searchBooks(searchQuery);

          const results = data?.results || [];
          setBooks(results);
          setIsSearched(true);

          if (Array.isArray(results) && results.length === 0) {
            fetchRecommendBook();
          } else {
            setRecommendBook(null);
          }
        } catch (error) {
          console.error("검색 오류: ", error);
          setBooks([]);
          setIsSearched(true);
          fetchRecommendBook();
        } finally {
          setLoading(false);
        }
      }, 500);

      return () => clearTimeout(timer);
    } else {
      setBooks([]);
      setIsSearched(false);
    }
  }, [searchQuery]);

  return (
    <PageWrap>
      <Header>
        <BackButton type="button" onClick={onBack}>
          <BackIcon width={24} height={24} />
        </BackButton>

        <SectionTitle>나눔된 책 검색</SectionTitle>
      </Header>

      <SearchWrapper>
        <SearchIcon fill={"#6F6F6F"} width={22} height={22} />
        <SearchInput
          type="text"
          placeholder="책 제목을 검색하세요."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          autoFocus
        />
      </SearchWrapper>

      {loading ? (
        <LoadingWrap>
          <LoadingPage isCompact={true} />
        </LoadingWrap>
      ) : (
        <BookListWrap>
          {books.length > 0 ? (
            books.map((book) => (
              <BookWrap
                key={book.isbn}
                onClick={() => handleBookClick(book.isbn)}
              >
                <Cover>
                  {book?.cover_url ? (
                    <CoverImg src={book?.cover_url} alt="" />
                  ) : (
                    <CoverFallback />
                  )}
                </Cover>

                <BookInfoWrap>
                  <Title>{book?.title || "-"}</Title>

                  <Sub>{book?.author || "-"}</Sub>
                  <Sub>{book?.publisher || "-"}</Sub>
                  <Sub>{book?.published_date || "-"}</Sub>
                </BookInfoWrap>
              </BookWrap>
            ))
          ) : isSearched ? (
            <SubWrap>
              <MessageWrap>
                <SearchIcon width={72} height={72} />
                <Notification>
                  <span className="highlight">"{searchQuery}"</span> 은 <br />
                  북작북작에 나눔되지 않았습니다.
                </Notification>
              </MessageWrap>

              <RecommendWrap>
                <Description>이 책은 어때요?</Description>
                <RecommendBookWrap>
                  {recommendBook ? (
                    <RecommendBook
                      src={recommendBook.cover_url}
                      onClick={() => handleBookClick(recommendBook.isbn)}
                    />
                  ) : (
                    <Notification>추천 도서를 불러올 수 없어요.</Notification>
                  )}
                </RecommendBookWrap>
                <BookTitle>{recommendBook?.title}</BookTitle>
              </RecommendWrap>
            </SubWrap>
          ) : null}
        </BookListWrap>
      )}
    </PageWrap>
  );
}

const LoadingWrap = styled.div`
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const PageWrap = styled.div`
  width: 100%;
  max-width: 600px;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  padding: 20px 20px 12px;
`;

const Header = styled.div`
  position: relative;
  height: 52px;
  display: grid;
  grid-template-columns: 56px 1fr 56px;
  align-items: center;
  margin-bottom: 8px;
  flex-shrink: 0;
`;

const BackButton = styled.button`
  background: none;
  border: 0;
  cursor: pointer;
`;

const SectionTitle = styled.div`
  font-size: 20px;
  font-weight: 500;
  text-align: center;
`;

const SearchWrapper = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  width: 100%;
  height: 42px;
  background-color: #e6f4f0;
  border: none;
  border-radius: 20px;
  padding: 4px 16px;
  margin-bottom: 16px;
  flex-shrink: 0;
`;

const SearchInput = styled.input`
  width: 100%;
  height: 42px;
  font-size: 16px;
  font-weight: 600;
  background-color: transparent;
  border: none;
  color: #6f6f6f;
  cursor: pointer;
  padding: 0px;

  &:focus {
    outline: none;
  }

  &::placeholder {
  color: #6f6f6f;
  font-size: 16px;
  font-weight: 600;
  }
`;

const BookListWrap = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 8px;
  overflow: hidden;
  overflow-y: auto;
`;

const BookWrap = styled.div`
  display: flex;
  flex-direction: row;
  height: 101px;
  gap: 8px;
  align-items: center;
  cursor: pointer;
`;

const Cover = styled.div`
  width: 79px;
  height: 101px;
  flex-shrink: 0;
`;

const CoverImg = styled.img`
  width: 100%;
  height: 100%;
  border-radius: 5px;
  object-fit: cover;
`;

const CoverFallback = styled.div`
  width: 79px;
  height: 101px;
  border-radius: 5px;
  background-color: #d9d9d9;
  overflow: hidden;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const BookInfoWrap = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const Title = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #000000;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 75%;
  width: 100%;
`;

const Sub = styled.div`
  font-size: 12px;
  font-weight: 400;
  color: #868686;
`;

const SubWrap = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 60px;
`;

const MessageWrap = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  margin-top: 60px;
  gap: 16px;
`;

const Notification = styled.div`
  font-size: 18px;
  font-weight: 500;
  color: #6f6f6f;

  .highlight {
    color: #000000;
  }
`;

const RecommendWrap = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 335px;
  min-height: 193px;
  background-color: #f4f4f4;

  border-radius: 5px;
  padding: 16px 0;
`;

const Description = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #6f6f6f;

  margin-bottom: 8px;
`;

const RecommendBookWrap = styled.div`
  width: 106px;
  height: 129px;
  margin-bottom: 8px;
`;

const RecommendBook = styled.img`
  width: 100%;
  height: 100%;
  border-radius: 5px;
  object-fit: cover;
`;

const BookTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #000000;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 80%;
  text-align: center;
`;
